// biome-ignore-all lint/security/noDangerouslySetInnerHtml: page bodies are Supervisor-authored and always rendered through sanitizeResourceHtml (defensive whitelist of dangerous tags/handlers/URLs).
// biome-ignore-all lint/suspicious/noArrayIndexKey: ResourceBody splits a sanitised HTML string into a fixed, order-stable list of fragments/widgets; index is the natural key.
// =============================================================================
// ResourceSection.jsx — read-only view of a Resources content section (db-109)
// =============================================================================
// Shared by Resources → Invoicing and Resources → Responders. Loads the section's
// pages, builds the nestable tree, shows an indented sidebar (click a title to
// read it), and renders the selected page's sanitised HTML. View-only — editing
// is under Manage → Resource Pages.
// =============================================================================

import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { listResourcePages } from "../../data/resourcePages";
import { showHelp } from "../../services/helpModal";
import { parseHelpKey } from "../../services/resourceHelpLinks";
import { sanitizeResourceHtml } from "../../services/resourceHtml";
import { signResourceImagesInHtml } from "../../services/resourceImageHtml";
import { buildResourceTree, flattenResourceTree } from "../../services/resourceTree";
import { GstCalculator } from "./GstCalculator";
import { ResourceSearchModal } from "./ResourceSearchModal.jsx";

// Shortcode a supervisor can type into a page body to embed a live utility. The
// content is sanitised HTML (scripts/handlers stripped), so interactive widgets
// can't be authored inline — instead we swap this token for the real component.
const GST_TOKEN = "[[gst-calculator]]";

// Render sanitised page HTML, swapping any [[gst-calculator]] token for the live
// GstCalculator component. A paragraph that holds ONLY the token is normalised
// first so we don't leave an empty <p></p> around the widget.
export function ResourceBody({ html, highlight }) {
  const ref = React.useRef(null);
  // The bucket is private (db-185): swap each inline resource-image src for a fresh
  // signed URL before rendering. Starts from the raw html (text shows immediately);
  // the effect replaces it with the signed version. Plain (image-free) pages come
  // back byte-identical, so this is a no-op for them — the GST/highlight logic below
  // is unchanged for them.
  const [signed, setSigned] = React.useState(html);
  React.useEffect(
    function () {
      let live = true;
      signResourceImagesInHtml(html).then(function (h) {
        if (live) setSigned(h);
      });
      return function () {
        live = false;
      };
    },
    [html]
  );

  const normalised = signed.replace(/<p>\s*\[\[gst-calculator\]\]\s*<\/p>/gi, GST_TOKEN);
  const parts = normalised.split(GST_TOKEN);

  // Search-jump highlight: wrap the FIRST occurrence of `highlight` in a <mark>
  // and scroll to it. A post-render DOM pass over the already-sanitised HTML — it
  // doesn't alter the stored markup and is wiped/redone whenever the page changes.
  React.useEffect(
    function () {
      const root = ref.current;
      const term = String(highlight || "").toLowerCase();
      if (!root || !term) return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      let node = walker.nextNode();
      while (node) {
        const idx = node.nodeValue.toLowerCase().indexOf(term);
        if (idx >= 0) {
          try {
            const range = document.createRange();
            range.setStart(node, idx);
            range.setEnd(node, idx + term.length);
            const mark = document.createElement("mark");
            mark.style.backgroundColor = "#FDE68A";
            range.surroundContents(mark);
            mark.scrollIntoView({ block: "center", behavior: "smooth" });
          } catch (_e) {
            /* range crosses element boundaries — skip */
          }
          break;
        }
        node = walker.nextNode();
      }
    },
    [signed, highlight]
  );

  // Intercept a click on a `#help=<key>` link and open the shared help modal (the
  // same text the (?) bubbles show) instead of navigating. Bound on the content
  // root via the ref (event delegation) rather than a JSX onClick, so it covers
  // every inline link in the rendered HTML without an a11y-interactive <div>.
  React.useEffect(function () {
    const root = ref.current;
    if (!root) return;
    function onClick(e) {
      const anchor = e.target?.closest?.("a[href]");
      if (!anchor) return;
      const key = parseHelpKey(anchor.getAttribute("href"));
      if (!key) return;
      e.preventDefault();
      showHelp(key);
    }
    root.addEventListener("click", onClick);
    return function () {
      root.removeEventListener("click", onClick);
    };
  }, []);

  return (
    <div ref={ref} className="resource-content text-[14px] text-zephyr-text leading-[1.7]">
      {parts.map(function (chunk, i) {
        return (
          <React.Fragment key={`rb-${i}`}>
            {chunk ? <div dangerouslySetInnerHTML={{ __html: chunk }} /> : null}
            {i < parts.length - 1 ? <GstCalculator /> : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Element styles for the rendered page HTML (Tailwind preflight resets these; the
// TipTap editor also emits tables / images / code blocks that need styling).
const RESOURCE_CONTENT_CSS = `
.resource-content p{margin:0 0 .7em}
.resource-content p:last-child{margin-bottom:0}
.resource-content h1{font-size:1.6em;font-weight:700;margin:1em 0 .3em}
.resource-content h2{font-size:1.35em;font-weight:700;margin:1em 0 .3em}
.resource-content h3{font-size:1.15em;font-weight:700;margin:1em 0 .3em}
.resource-content>div>:first-child{margin-top:0}
.resource-content ul{list-style:disc;padding-left:1.4em}
.resource-content ol{list-style:decimal;padding-left:1.4em}
.resource-content blockquote{border-left:3px solid #CFE0DE;margin:.4em 0;padding-left:12px;color:#4a5c59}
.resource-content a{color:#00695C;text-decoration:underline}
.resource-content pre{background:#1A2E2B;color:#E0F2F1;padding:10px 12px;border-radius:6px;overflow-x:auto;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:13px}
.resource-content code{font-family:ui-monospace,Menlo,Consolas,monospace}
.resource-content img{max-width:100%;height:auto;border-radius:4px}
.resource-content>div{display:flow-root}
.resource-content table{border-collapse:collapse;margin:8px 0}
.resource-content td,.resource-content th{border:1px solid #CFD8DC;padding:5px 9px;vertical-align:top}
.resource-content th{background:#EEF4F3;font-weight:700;text-align:left}
`;

export function ResourceSection({ section, heading }) {
  const [rows, setRows] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState(null);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageParam = searchParams.get("page");
  const highlightTerm = searchParams.get("q") || "";

  // Load the section's pages (selection handled by the effect below).
  React.useEffect(
    function () {
      let live = true;
      listResourcePages(section).then(function (r) {
        if (live) setRows(r || []);
      });
      return function () {
        live = false;
      };
    },
    [section]
  );

  // Selection: honour a ?page= deep-link (a search jump) when it's a real page;
  // otherwise default to the first page. Re-runs on param change so a same-section
  // jump re-selects without a remount.
  React.useEffect(
    function () {
      if (!rows) return;
      if (pageParam && rows.some((r) => r.id === pageParam)) {
        setSelectedId(pageParam);
      } else {
        setSelectedId(
          (cur) =>
            cur ||
            (rows.length > 0 ? flattenResourceTree(buildResourceTree(rows))[0].node.id : null)
        );
      }
    },
    [rows, pageParam]
  );

  // Keyboard: Ctrl/Cmd+K (and "/" when not typing in a field) opens search.
  React.useEffect(function () {
    function onKey(e) {
      const meta = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      const el = e.target;
      const typing =
        el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (meta || (e.key === "/" && !typing)) {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return function () {
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  function openSearchResult(resultSection, pageId, query) {
    setSearchOpen(false);
    const qs = `?page=${encodeURIComponent(pageId)}${query ? `&q=${encodeURIComponent(query)}` : ""}`;
    navigate(`/${resultSection}${qs}`);
  }

  const flat = React.useMemo(
    function () {
      return rows ? flattenResourceTree(buildResourceTree(rows)) : [];
    },
    [rows]
  );
  const selected = React.useMemo(
    function () {
      return (rows || []).find((r) => r.id === selectedId) || null;
    },
    [rows, selectedId]
  );

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      {/* Rendered page HTML needs explicit element styles — Tailwind's preflight
          resets headings/lists, and tables/images come from the TipTap editor. */}
      <style>{RESOURCE_CONTENT_CSS}</style>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h1 className="m-0 text-[22px] text-zephyr-text">{heading}</h1>
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          title="Search Resources (Ctrl/Cmd+K)"
          className="flex items-center gap-2 border border-zephyr-border rounded-[8px] px-3 py-2 text-[13px] text-zephyr-muted bg-white hover:bg-zephyr-panel-bg cursor-pointer"
        >
          <span aria-hidden="true">🔍</span>
          Search
          <kbd className="text-[11px] border border-zephyr-border rounded px-1">Ctrl K</kbd>
        </button>
      </div>
      {rows === null ? (
        <div className="text-zephyr-muted p-5">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-zephyr-border rounded-[10px] p-10 text-center text-zephyr-muted text-[14px]">
          No pages here yet.
        </div>
      ) : (
        <div className="grid gap-6" style={{ gridTemplateColumns: "minmax(200px, 260px) 1fr" }}>
          {/* Tree sidebar */}
          <nav className="border border-zephyr-border rounded-[10px] p-2 self-start bg-white">
            {flat.map(function (f) {
              const active = f.node.id === selectedId;
              return (
                <button
                  key={f.node.id}
                  type="button"
                  onClick={function () {
                    setSelectedId(f.node.id);
                  }}
                  className={`block w-full text-left text-[13px] rounded-[6px] py-[6px] pr-2 cursor-pointer ${
                    active
                      ? "bg-zephyr-teal text-white font-semibold"
                      : "text-zephyr-text hover:bg-zephyr-panel-bg"
                  }`}
                  style={{
                    paddingLeft: 10 + f.depth * 16,
                    border: "none",
                    background: active ? undefined : "transparent",
                  }}
                >
                  {f.node.title}
                </button>
              );
            })}
          </nav>
          {/* Content */}
          <article className="border border-zephyr-border rounded-[10px] p-6 bg-white min-w-0">
            {selected ? (
              <>
                <h2 className="m-0 mb-4 text-[18px] text-zephyr-text">{selected.title}</h2>
                <ResourceBody
                  html={sanitizeResourceHtml(selected.body || "")}
                  highlight={selected.id === pageParam ? highlightTerm : ""}
                />
              </>
            ) : (
              <div className="text-zephyr-muted">Select a page.</div>
            )}
          </article>
        </div>
      )}
      <ResourceSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        currentSection={section}
        onOpen={openSearchResult}
      />
    </div>
  );
}
